/** @module AuthClient */
import {
  AnonymousIdentity,
  type DerEncodedPublicKey,
  type Identity,
  type Signature,
  SignIdentity,
} from '@dfinity/agent';
import {
  Delegation,
  DelegationChain,
  isDelegationValid,
  DelegationIdentity,
  Ed25519KeyIdentity,
  ECDSAKeyIdentity,
  type SignedDelegation,
} from '@dfinity/identity';
import { Principal } from '@dfinity/principal';
import { IdleManager, type IdleManagerOptions } from './idleManager';
import {
  type AuthClientStorage,
  IdbStorage,
  isBrowser,
  KEY_STORAGE_DELEGATION,
  KEY_STORAGE_KEY,
  KEY_VECTOR,
  LocalStorage,
} from './storage';
import { fromByteArray, toByteArray } from 'base64-js';

export {
  IdbStorage,
  LocalStorage,
  KEY_STORAGE_DELEGATION,
  KEY_STORAGE_KEY,
} from './storage';
export { IdbKeyVal, type DBCreateOptions } from './db';

const IDENTITY_PROVIDER_DEFAULT = 'https://id.ai';
const IDENTITY_PROVIDER_ENDPOINT = '#authorize';

const ECDSA_KEY_LABEL = 'ECDSA';
const ED25519_KEY_LABEL = 'Ed25519';
type BaseKeyType = typeof ECDSA_KEY_LABEL | typeof ED25519_KEY_LABEL;

const INTERRUPT_CHECK_INTERVAL = 500;

export const ERROR_USER_INTERRUPT = 'UserInterrupt';

/**
 * List of options for creating an {@link AuthClient}.
 */
export interface AuthClientCreateOptions {
  /**
   * An identity to use as the base
   */
  identity?: SignIdentity | ECDSAKeyIdentity;
  /**
   * Optional storage with get, set, and remove. Uses {@link IdbStorage} by default
   */
  storage?: AuthClientStorage;
  /**
   * type to use for the base key
   * @default 'ECDSA'
   * If you are using a custom storage provider that does not support CryptoKey storage,
   * you should use 'Ed25519' as the key type, as it can serialize to a string
   */
  keyType?: BaseKeyType;

  /**
   * Options to handle idle timeouts
   * @default after 30 minutes, invalidates the identity
   */
  idleOptions?: IdleOptions;
}

export interface IdleOptions extends IdleManagerOptions {
  /**
   * Disables idle functionality for {@link IdleManager}
   * @default false
   */
  disableIdle?: boolean;

  /**
   * Disables default idle behavior - call logout & reload window
   * @default false
   */
  disableDefaultIdleCallback?: boolean;
}

export * from './idleManager';

export interface AuthClientLoginOptions {
  /**
   * Identity provider
   * @default "https://id.ai"
   */
  identityProvider?: string | URL;
  /**
   * Expiration of the authentication in nanoseconds
   * @default  BigInt(8) hours * BigInt(3_600_000_000_000) nanoseconds
   */
  maxTimeToLive?: bigint;
  /**
   * Origin for Identity Provider to use while generating the delegated identity. For II, the derivation origin must authorize this origin by setting a record at `<derivation-origin>/.well-known/ii-alternative-origins`.
   * @see https://github.com/dfinity/internet-identity/blob/main/docs/internet-identity-spec.adoc
   */
  derivationOrigin?: string | URL;
  /**
   * Auth Window feature config string
   * @example "toolbar=0,location=0,menubar=0,width=500,height=500,left=100,top=100"
   */
  windowOpenerFeatures?: string;
  redirectContext?: {
    /**
     * The param in the callback URL to use for the response
     */
    param: string;
    /**
     * The callback url
     */
    url: string;
  };
  /**
   * Callback once login has completed
   */
  onSuccess?: (() => void) | (() => Promise<void>);
  /**
   * Callback in case authentication fails
   */
  onError?: ((error?: string) => void) | ((error?: string) => Promise<void>);
  /**
   * Callback in case authentication is interrupted. The payload in this case can be provider-specific.
   */
  onInterrupt?: (payload?: unknown) => void;
}

interface AuthRequestBase<T, U> {
  kind: 'authorize-client';
  sessionPublicKey: T;
  maxTimeToLive?: U;
  derivationOrigin?: string;
}

type InternetIdentityAuthRequest = AuthRequestBase<Uint8Array, bigint>;
interface RedirectAuthRequest extends AuthRequestBase<string, string> {
  redirectUri: string;
  responseParam: string;
}

interface AuthResponseSuccessBase<T, U> {
  kind: 'authorize-client-success';
  delegations: {
    delegation: T;
    signature: U;
  }[];
  userPublicKey: U;
}

interface InternetIdentityAuthResponseDelegation {
  pubkey: Uint8Array;
  expiration: bigint;
  targets?: Principal[];
}

type InternetIdentityAuthResponseSuccess = AuthResponseSuccessBase<
  InternetIdentityAuthResponseDelegation,
  Uint8Array
>;

interface RedirectAuthResponseDelegation
  extends Omit<
    Record<keyof InternetIdentityAuthResponseDelegation, string>,
    'targets'
  > {
  targets?: string[];
}

type RedirectAuthResponseSuccess = AuthResponseSuccessBase<
  RedirectAuthResponseDelegation,
  string
>;

interface AuthReadyMessage {
  kind: 'authorize-ready';
}

interface AuthResponseFailure {
  kind: 'authorize-client-failure';
  text: string;
}

interface AuthResponseInterrupt {
  kind: 'authorize-client-interrupt';
  payload?: unknown;
}

type IdentityServiceResponseMessage = AuthReadyMessage | AuthResponse;

type AuthResponse =
  | InternetIdentityAuthResponseSuccess
  | AuthResponseFailure
  | AuthResponseInterrupt;

type RedirectResponse =
  | RedirectAuthResponseSuccess
  | AuthResponseFailure
  | AuthResponseInterrupt;

/**
 * Tool to manage authentication and identity
 * @see {@link AuthClient}
 */
export class AuthClient {
  /**
   * Create an AuthClient to manage authentication and identity
   * @constructs {@link AuthClient}
   * @param {AuthClientCreateOptions} options
   * @see {@link AuthClientCreateOptions}
   * @param options.identity Optional Identity to use as the base
   * @see {@link SignIdentity}
   * @param options.storage Storage mechanism for delegration credentials
   * @see {@link AuthClientStorage}
   * @param options.keyType Type of key to use for the base key
   * @param {IdleOptions} options.idleOptions Configures an {@link IdleManager}
   * @see {@link IdleOptions}
   * Default behavior is to clear stored identity and reload the page when a user goes idle, unless you set the disableDefaultIdleCallback flag or pass in a custom idle callback.
   * @example
   * const authClient = await AuthClient.create({
   *   idleOptions: {
   *     disableIdle: true
   *   }
   * })
   */
  public static async create(
    options: {
      /**
       * An {@link Identity} to use as the base.
       *  By default, a new {@link AnonymousIdentity}
       */
      identity?: SignIdentity;
      /**
       * {@link AuthClientStorage}
       * @description Optional storage with get, set, and remove. Uses {@link IdbStorage} by default
       */
      storage?: AuthClientStorage;
      /**
       * type to use for the base key
       * @default 'ECDSA'
       * If you are using a custom storage provider that does not support CryptoKey storage,
       * you should use 'Ed25519' as the key type, as it can serialize to a string
       */
      keyType?: BaseKeyType;
      /**
       * Options to handle idle timeouts
       * @default after 10 minutes, invalidates the identity
       */
      idleOptions?: IdleOptions;
      /**
       * The context if received by redirect flow
       */
      redirectContext?: {
        /**
         * The param in the callback URL to use for the response
         */
        param: string;
        /**
         * The search parameters
         */
        search: string;
      };
    } = {},
  ): Promise<AuthClient> {
    const storage = options.storage ?? new IdbStorage();
    const keyType = options.keyType ?? ECDSA_KEY_LABEL;

    let key: null | SignIdentity | ECDSAKeyIdentity = null;
    let redirectResponse: RedirectResponse | undefined = undefined;

    if (options.redirectContext) {
      let params = new URLSearchParams(options.redirectContext.search);
      if (params.has(options.redirectContext.param)) {
        try {
          redirectResponse = JSON.parse(
            decodeURIComponent(params.get(options.redirectContext.param)!),
          );
        } catch (e) {
          console.error('Error parsing auth response: ', e);
        }
      }
    }

    if (options.identity) {
      key = options.identity;
    } else {
      let maybeIdentityStorage = await storage.get(KEY_STORAGE_KEY);
      if (!maybeIdentityStorage && isBrowser) {
        // Attempt to migrate from localstorage
        try {
          const fallbackLocalStorage = new LocalStorage();
          const localChain = await fallbackLocalStorage.get(
            KEY_STORAGE_DELEGATION,
          );
          const localKey = await fallbackLocalStorage.get(KEY_STORAGE_KEY);
          // not relevant for Ed25519
          if (localChain && localKey && keyType === ECDSA_KEY_LABEL) {
            console.log(
              'Discovered an identity stored in localstorage. Migrating to IndexedDB',
            );
            await storage.set(KEY_STORAGE_DELEGATION, localChain);
            await storage.set(KEY_STORAGE_KEY, localKey);

            maybeIdentityStorage = localChain;
            // clean up
            await fallbackLocalStorage.remove(KEY_STORAGE_DELEGATION);
            await fallbackLocalStorage.remove(KEY_STORAGE_KEY);
          }
        } catch (error) {
          console.error(
            'error while attempting to recover localstorage: ' + error,
          );
        }
      }
      if (maybeIdentityStorage) {
        try {
          if (typeof maybeIdentityStorage === 'object') {
            if (
              keyType === ED25519_KEY_LABEL &&
              typeof maybeIdentityStorage === 'string'
            ) {
              key = await Ed25519KeyIdentity.fromJSON(maybeIdentityStorage);
            } else {
              key = await ECDSAKeyIdentity.fromKeyPair(maybeIdentityStorage);
            }
          } else if (typeof maybeIdentityStorage === 'string') {
            // This is a legacy identity, which is a serialized Ed25519KeyIdentity.
            key = Ed25519KeyIdentity.fromJSON(maybeIdentityStorage);
          }
        } catch (e) {
          // Ignore this, this means that the localStorage value isn't a valid Ed25519KeyIdentity or ECDSAKeyIdentity
          // serialization.
        }
      }
    }

    let identity = new AnonymousIdentity();
    let chain: null | DelegationChain = null;
    if (key) {
      try {
        if (redirectResponse) {
          if (redirectResponse.kind === 'authorize-client-success') {
            const publicKeyb64 = fromByteArray(
              new Uint8Array(key!.getPublicKey().toDer()),
            );
            const signedPublicKey =
              redirectResponse.delegations[0].delegation.pubkey;
            // the first delegation should match the session public key
            const matched =
              redirectResponse.delegations.length > 0 &&
              publicKeyb64 == signedPublicKey;
            if (!matched) {
              throw new Error(
                `Delegation ${publicKeyb64} did not match original session key ${signedPublicKey}`,
              );
            }

            const delegations = redirectResponse.delegations.map(
              _transformRedirectDelegations,
            );

            const delegationChain = DelegationChain.fromDelegations(
              delegations,
              toByteArray(redirectResponse.userPublicKey)
                .buffer as DerEncodedPublicKey,
            );

            await storage.set(
              KEY_STORAGE_DELEGATION,
              JSON.stringify(delegationChain.toJSON()),
            );
          }
        }

        const chainStorage = await storage.get(KEY_STORAGE_DELEGATION);
        if (typeof chainStorage === 'object' && chainStorage !== null) {
          throw new Error(
            'Delegation chain is incorrectly stored. A delegation chain should be stored as a string.',
          );
        }

        if (options.identity) {
          identity = options.identity;
        } else if (chainStorage) {
          chain = DelegationChain.fromJSON(chainStorage);

          // Verify that the delegation isn't expired.
          if (!isDelegationValid(chain)) {
            await _deleteStorage(storage);
            key = null;
          } else {
            identity = DelegationIdentity.fromDelegation(key, chain);
          }
        }
      } catch (e) {
        console.error(e);
        // If there was a problem loading the chain, delete the key.
        await _deleteStorage(storage);
        key = null;
      }
    }
    let idleManager: IdleManager | undefined = undefined;
    // if there is a delegation chain or provided identity, setup idleManager
    if (!options.idleOptions?.disableIdle && (chain || options.identity)) {
      idleManager = IdleManager.create(options.idleOptions);
    }

    if (!key) {
      // Create a new key (whether or not one was in storage).
      if (keyType === ED25519_KEY_LABEL) {
        key = Ed25519KeyIdentity.generate();
        await storage.set(
          KEY_STORAGE_KEY,
          JSON.stringify((key as Ed25519KeyIdentity).toJSON()),
        );
      } else {
        if (options.storage && keyType === ECDSA_KEY_LABEL) {
          console.warn(
            `You are using a custom storage provider that may not support CryptoKey storage. If you are using a custom storage provider that does not support CryptoKey storage, you should use '${ED25519_KEY_LABEL}' as the key type, as it can serialize to a string`,
          );
        }
        key = await ECDSAKeyIdentity.generate();
        await storage.set(
          KEY_STORAGE_KEY,
          (key as ECDSAKeyIdentity).getKeyPair(),
        );
      }
    }

    return new this(
      identity,
      key,
      chain,
      storage,
      idleManager,
      options,
      redirectResponse,
    );
  }

  public redirectResponse() {
    return this._redirectResponse;
  }

  protected constructor(
    private _identity: Identity,
    private _key: SignIdentity,
    private _chain: DelegationChain | null,
    private _storage: AuthClientStorage,
    public idleManager: IdleManager | undefined,
    private _createOptions: AuthClientCreateOptions | undefined,
    private _redirectResponse?: RedirectResponse,
    // A handle on the IdP window.
    private _idpWindow?: Window,
    // The event handler for processing events from the IdP.
    private _eventHandler?: (event: MessageEvent) => void,
  ) {
    const idleOptions = _createOptions?.idleOptions;
    this._registerDefaultIdleCallback();
  }

  private _registerDefaultIdleCallback() {
    const idleOptions = this._createOptions?.idleOptions;
    /**
     * Default behavior is to clear stored identity and reload the page.
     * By either setting the disableDefaultIdleCallback flag or passing in a custom idle callback, we will ignore this config
     */
    if (!idleOptions?.onIdle && !idleOptions?.disableDefaultIdleCallback) {
      this.idleManager?.registerCallback(() => {
        console.log('User is idle. Logging out.');
        this.logout();
        location.reload();
      });
    }
  }

  private async _handleSuccess(
    message: InternetIdentityAuthResponseSuccess,
    onSuccess?: (() => void) | (() => Promise<void>),
  ) {
    const delegations = message.delegations.map((signedDelegation) => {
      return {
        delegation: new Delegation(
          signedDelegation.delegation.pubkey,
          signedDelegation.delegation.expiration,
          signedDelegation.delegation.targets,
        ),
        signature: signedDelegation.signature.buffer as Signature,
      };
    });

    const delegationChain = DelegationChain.fromDelegations(
      delegations,
      message.userPublicKey.buffer as DerEncodedPublicKey,
    );

    const key = this._key;
    if (!key) {
      return;
    }

    this._chain = delegationChain;
    this._identity = DelegationIdentity.fromDelegation(key, this._chain);

    this._idpWindow?.close();
    const idleOptions = this._createOptions?.idleOptions;
    // create the idle manager on a successful login if we haven't disabled it
    // and it doesn't already exist.
    if (!this.idleManager && !idleOptions?.disableIdle) {
      this.idleManager = IdleManager.create(idleOptions);
      this._registerDefaultIdleCallback();
    }

    this._removeEventListener();
    delete this._idpWindow;

    if (this._chain) {
      await this._storage.set(
        KEY_STORAGE_DELEGATION,
        JSON.stringify(this._chain.toJSON()),
      );
    }

    // onSuccess should be the last thing to do to avoid consumers
    // interfering by navigating or refreshing the page
    onSuccess?.();
  }

  public getIdentity(): Identity {
    return this._identity;
  }

  public async isAuthenticated(): Promise<boolean> {
    return (
      !this.getIdentity().getPrincipal().isAnonymous() && this._chain !== null
    );
  }

  /**
   * AuthClient Login -
   * Opens up a new window to authenticate with Internet Identity
   * @param {AuthClientLoginOptions} options
   * @param options.identityProvider Identity provider
   * @param options.maxTimeToLive Expiration of the authentication in nanoseconds
   * @param options.derivationOrigin Origin for Identity Provider to use while generating the delegated identity
   * @param options.windowOpenerFeatures Configures the opened authentication window
   * @param options.redirectContext Specify the redirect context for the redirect flow
   * @param options.onSuccess Callback once login has completed
   * @param options.onError Callback in case authentication fails
   * @param options.onInterrupt Callback in case authentication is interrupted. The payload in this case can be provider-specific.
   * @example
   * const authClient = await AuthClient.create();
   * authClient.login({
   *  identityProvider: 'http://<canisterID>.localhost:8000',
   *  maxTimeToLive: BigInt (7) * BigInt(24) * BigInt(3_600_000_000_000), // 1 week
   *  windowOpenerFeatures: "toolbar=0,location=0,menubar=0,width=500,height=500,left=100,top=100",
   *  onSuccess: () => {
   *    console.log('Login Successful!');
   *  },
   *  onError: (error) => {
   *    console.error('Login Failed: ', error);
   *  }
   *  onInterrupt: (payload) => {
   *    console.error('Login interrupted');
   *  }
   * });
   */
  public async login(options?: {
    /**
     * Identity provider
     * @default "https://id.ai"
     */
    identityProvider?: string | URL;
    /**
     * Expiration of the authentication in nanoseconds
     * @default  BigInt(8) hours * BigInt(3_600_000_000_000) nanoseconds
     */
    maxTimeToLive?: bigint;
    /**
     * Auth Window feature config string
     * @example "toolbar=0,location=0,menubar=0,width=500,height=500,left=100,top=100"
     */
    /**
     * Origin for Identity Provider to use while generating the delegated identity. For II, the derivation origin must authorize this origin by setting a record at `<derivation-origin>/.well-known/ii-alternative-origins`.
     * @see https://github.com/dfinity/internet-identity/blob/main/docs/internet-identity-spec.adoc
     */
    derivationOrigin?: string | URL;
    windowOpenerFeatures?: string;
    redirectContext?: {
      /**
       * Use redirect flow and redirect back to this URL after authentication
       */
      url: string;
      /**
       * Use redirect flow and send response back in this parameter after authentication
       */
      param: string;
    };
    /**
     * Callback once login has completed
     */
    onSuccess?: (() => void) | (() => Promise<void>);
    /**
     * Callback in case authentication fails
     */
    onError?: ((error?: string) => void) | ((error?: string) => Promise<void>);
    /**
     * Callback in case authentication is interrupted. The payload in this case can be provider-specific.
     */
    onInterrupt?: (payload?: unknown) => void;
  }): Promise<void> {
    // Set default maxTimeToLive to 8 hours
    const defaultTimeToLive =
      /* hours */ BigInt(8) * /* nanoseconds */ BigInt(3_600_000_000_000);

    // Create the URL of the IDP. (e.g. https://XXXX/#authorize)
    const identityProviderUrl = new URL(
      options?.identityProvider?.toString() || IDENTITY_PROVIDER_DEFAULT,
    );
    // Set the correct hash if it isn't already set.
    identityProviderUrl.hash = IDENTITY_PROVIDER_ENDPOINT;

    // If `login` has been called previously, then close/remove any previous windows
    // and event listeners.
    this._idpWindow?.close();
    this._removeEventListener();

    // Add an event listener to handle responses.
    this._eventHandler = this._getEventHandler(identityProviderUrl, {
      maxTimeToLive: options?.maxTimeToLive ?? defaultTimeToLive,
      ...options,
    });
    window.addEventListener('message', this._eventHandler);

    if (options?.redirectContext) {
      this._sendRedirectRequest(identityProviderUrl, options);
    } else {
      // Open a new window with the IDP provider.
      this._idpWindow =
        window.open(
          identityProviderUrl.toString(),
          'idpWindow',
          options?.windowOpenerFeatures,
        ) ?? undefined;

      // Check if the _idpWindow is closed by user.
      const checkInterruption = (): void => {
        // The _idpWindow is opened and not yet closed by the client
        if (this._idpWindow) {
          if (this._idpWindow.closed) {
            this._handleFailure(ERROR_USER_INTERRUPT, options?.onError);
          } else {
            setTimeout(checkInterruption, INTERRUPT_CHECK_INTERVAL);
          }
        }
      };
      checkInterruption();
    }
  }

  private _sendRedirectRequest(
    identityProviderUrl: URL,
    options?: AuthClientLoginOptions,
  ) {
    const request: RedirectAuthRequest = {
      kind: 'authorize-client',
      sessionPublicKey: fromByteArray(
        new Uint8Array(this._key?.getPublicKey().toDer() as ArrayBuffer),
      ),
      maxTimeToLive: options?.maxTimeToLive?.toString(),
      derivationOrigin: options?.derivationOrigin?.toString(),
      redirectUri: options!.redirectContext?.url!,
      responseParam: options!.redirectContext?.param!,
    };
    const request_string = encodeURIComponent(JSON.stringify(request));
    identityProviderUrl.search = `auth_request=${request_string}`;
    window.location.href = identityProviderUrl.toString();
  }

  private _getEventHandler(
    identityProviderUrl: URL,
    options?: AuthClientLoginOptions,
  ) {
    return async (event: MessageEvent) => {
      if (event.origin !== identityProviderUrl.origin) {
        console.warn(
          `WARNING: expected origin '${identityProviderUrl.origin}', got '${event.origin}' (ignoring)`,
        );
        return;
      }

      const message = event.data as IdentityServiceResponseMessage;

      switch (message.kind) {
        case 'authorize-ready': {
          // IDP is ready. Send a message to request authorization.
          const request: InternetIdentityAuthRequest = {
            kind: 'authorize-client',
            sessionPublicKey: new Uint8Array(
              this._key?.getPublicKey().toDer() as ArrayBuffer,
            ),
            maxTimeToLive: options?.maxTimeToLive,
            derivationOrigin: options?.derivationOrigin?.toString(),
          };
          this._idpWindow?.postMessage(request, identityProviderUrl.origin);
          break;
        }
        case 'authorize-client-success':
          // Create the delegation chain and store it.
          try {
            await this._handleSuccess(message, options?.onSuccess);
          } catch (err) {
            this._handleFailure((err as Error).message, options?.onError);
          }
          break;
        case 'authorize-client-failure':
          this._handleFailure(message.text, options?.onError);
          break;
        case 'authorize-client-interrupt':
          this._handleInterrupt(message.payload, options?.onInterrupt);
          break;
        default:
          break;
      }
    };
  }

  private _handleFailure(
    errorMessage?: string,
    onError?: (error?: string) => void,
  ): void {
    this._idpWindow?.close();
    onError?.(errorMessage);
    this._removeEventListener();
    delete this._idpWindow;
  }

  private _handleInterrupt(
    payload?: unknown,
    onInterrupt?: (payload?: unknown) => void,
  ): void {
    this._idpWindow?.close();
    onInterrupt?.(payload);
    this._removeEventListener();
    delete this._idpWindow;
  }

  private _removeEventListener() {
    if (this._eventHandler) {
      window.removeEventListener('message', this._eventHandler);
    }
    this._eventHandler = undefined;
  }

  public async logout(options: { returnTo?: string } = {}): Promise<void> {
    await _deleteStorage(this._storage);

    // Reset this auth client to a non-authenticated state.
    this._identity = new AnonymousIdentity();
    this._chain = null;

    if (options.returnTo && !options.returnTo.startsWith('http')) {
      try {
        window.location.href = options.returnTo;
      } catch (e) {
        window.location.href = options.returnTo;
      }
    }
  }
}

function _transformRedirectDelegations(
  signedDelegation: RedirectAuthResponseSuccess['delegations'][0],
): SignedDelegation {
  const sessionKey = toByteArray(signedDelegation.delegation.pubkey);
  return {
    delegation: new Delegation(
      sessionKey,
      BigInt(signedDelegation.delegation.expiration),
      signedDelegation.delegation.targets?.map((target) => {
        return Principal.fromText(target);
      }),
    ),
    signature: toByteArray(signedDelegation.signature).buffer as Signature,
  };
}

async function _deleteStorage(storage: AuthClientStorage) {
  await storage.remove(KEY_STORAGE_KEY);
  await storage.remove(KEY_STORAGE_DELEGATION);
  await storage.remove(KEY_VECTOR);
}
